import { ReactNode } from "react";

import Tooltip from "ds/components/Tooltip";
import { TooltipProps, TooltipReferenceProps } from "ds/components/Tooltip/types";
import { BillingTierFeature, Template } from "types/generated";
import useTierFeature from "views/Account/hooks/useTierFeature";
import { Action } from "types/generated";
import { AccessResult, checkAllowedAction } from "utils/access";
import useCanCreateTemplate from "shared/Templates/access/useCanCreateTemplate";

import { TemplateActions } from "./types";

type TemplatesFeatureGateTooltipProps = {
  on: (props: TooltipReferenceProps & { isDisabled: boolean }) => ReactNode;
  action: TemplateActions;
  template?: Template;
  tooltipPlacement?: TooltipProps["placement"];
  disabledMessage?: string;
};

const getTemplateFeatureGatingMessage = (action: TemplatesFeatureGateTooltipProps["action"]) => {
  switch (action) {
    case TemplateActions.CreateTemplate:
      return "Templates are not supported by your plan. Upgrade to the higher plan to be able to create Templates.";
    case TemplateActions.EditTemplate:
      return "Templates are not supported by your plan. Upgrade to the higher plan to be able to edit Templates.";
  }
};

const getTemplateActionAccessResult = (
  action: TemplatesFeatureGateTooltipProps["action"],
  canCreateTemplate: AccessResult,
  canManageTemplate: AccessResult
) => {
  switch (action) {
    case TemplateActions.CreateTemplate:
      return canCreateTemplate;
    case TemplateActions.EditTemplate:
      return canManageTemplate;
  }
};

const TemplateFeatureGateTooltip = ({
  on,
  action,
  template,
  tooltipPlacement,
  disabledMessage,
}: TemplatesFeatureGateTooltipProps) => {
  const isFeatureAvailableInPlan = useTierFeature(BillingTierFeature.Blueprints);
  const canCreateTemplate = useCanCreateTemplate();
  const canManageTemplate = checkAllowedAction(template, Action.UpdateBlueprintsVersionedGroup);

  const accessResult = getTemplateActionAccessResult(action, canCreateTemplate, canManageTemplate);

  const isDisabled = !isFeatureAvailableInPlan || !accessResult.result || !!disabledMessage;
  const tooltipMessage = !isFeatureAvailableInPlan
    ? getTemplateFeatureGatingMessage(action)
    : accessResult.message || disabledMessage;
  const isTooltipActive = !isFeatureAvailableInPlan || !!accessResult.message || !!disabledMessage;

  return (
    <Tooltip
      on={(props) => on({ ...props, isDisabled })}
      active={isTooltipActive}
      widthMode="maxWidthSm"
      placement={tooltipPlacement}
    >
      {tooltipMessage}
    </Tooltip>
  );
};

export default TemplateFeatureGateTooltip;
